import apiClient from '@/lib/apiClient'
import { endpoints } from './endPoint'
import type { SubEventTicket } from '@/components/sub-event/AddEditSubEvent'
import { SORT_ORDER } from '@/constants'
import type { SortingState } from '@tanstack/react-table'

/* Fetch Sub Events */

export const fetchSubEvents = async (params: {
  limit?: number
  page?: number
  search?: string
  sort?: SortingState
  status?: string[]
  startDate?: string
  endDate?: string
  categories?: string[]
  fetchNamesOnly?: boolean
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.limit) queryParams.append('limit', params?.limit?.toString())
    if (params.page) queryParams.append('page', params?.page?.toString())
    if (params.search) queryParams.append('search', params?.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.status) {
      params?.status?.forEach(s => {
        queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
      })
    }
    if (params.categories) {
      params.categories?.forEach(category => {
        queryParams.append('categories', category)
      })
    }
    if (params.startDate) {
      queryParams.append('startDate', params.startDate)
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate)
    }
    if (params.fetchNamesOnly) {
      queryParams.append('fetchNamesOnly', 'true')
    }

    const response = await apiClient.get(
      `${endpoints.subEvent.getSubEvents}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching events:', error)
    throw error
  }
}

/* Create Sub Event */

export interface EventSponsorShipPayloadType {
  id?: string
  name: string
  price?: number | string | null
}

export interface createSubEventPayload {
  eventId: string
  name: string
  description: string
  type: string
  maxAttendeesLimit: number
  tickets: Array<SubEventTicket>
  sponsorshipPackages: Array<EventSponsorShipPayloadType>
}

export const createSubEvent = async (payload: createSubEventPayload) => {
  try {
    const response = await apiClient.post(
      endpoints.subEvent.createSubEvent,
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error while creating sub-events:', error)
    throw error
  }
}

/* Get Sub Event By ID */

export const getSubEventById = async (id: string) => {
  try {
    const response = await apiClient.get(endpoints.subEvent.getSubEventById(id))
    return response.data
  } catch (error) {
    console.error('Error fetching sub-event by ID:', error)
    throw error
  }
}

/* Update Sub Event */

export const updateSubEvent = async (
  id: string,
  payload: createSubEventPayload
) => {
  try {
    const response = await apiClient.put(
      endpoints.subEvent.updateSubEvent(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error while updating sub-event:', error)
    throw error
  }
}

/* Delete Sub Event */

export const deleteSubEvent = async (id: string) => {
  try {
    const response = await apiClient.delete(
      endpoints.subEvent.deleteSubEvent(id)
    )
    return response.data
  } catch (error) {
    console.error('Error while deleting sub-event:', error)
    throw error
  }
}
